import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import HeartRain from "./HeartRain"; 

// Local hero images - **Ensure these paths are correct!**
import hero1 from "../assets/hero1.png";
import hero2 from "../assets/hero2.png";
import hero3 from "../assets/hero3.png";
import hero4 from "../assets/hero4.png";

export default function Hero() {
  const images = [hero1, hero2, hero3, hero4];
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  // Auto slideshow with 4-second interval for the spring animation
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1); 
      setIndex((prev) => (prev + 1) % images.length);
    }, 4000); 
    return () => clearInterval(timer);
  }, [images.length]);

  // ✨ DEPTH-POP VARIANTS: Uses scale, rotation, and spring physics
  const depthPopVariants = {
    enter: (dir) => ({ 
      // Image enters scaled down, slightly off-center, and rotated
      scale: 0.9, 
      opacity: 0, 
      rotate: dir > 0 ? 3 : -3, 
      x: dir > 0 ? 50 : -50,
    }),
    center: { 
      // Snaps to center with a spring effect
      scale: 1, 
      opacity: 1, 
      rotate: 0, 
      x: 0,
      transition: { 
        type: "spring", 
        stiffness: 150, 
        damping: 25,    
        restDelta: 0.001 
      } 
    },
    exit: (dir) => ({ 
      // Image exits by quickly fading, shrinking, and rotating away
      scale: 0.8, 
      opacity: 0, 
      rotate: dir > 0 ? -5 : 5, 
      transition: { 
        duration: 0.35
      } 
    }),
  };

  const scrollToProducts = () => {
    // 🛑 This requires an element with id="products" to be present elsewhere in your application.
    const s = document.querySelector("#products"); 
    if (s) s.scrollIntoView({ behavior: "smooth", block: "start" }); 
  };

  return (
    <section className="relative w-full min-h-screen bg-gradient-to-br from-[#FFF5F8] to-white overflow-hidden flex items-center">
      
      {/* ❤️ Heart Rain Background Effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 opacity-70">
        <HeartRain />
      </div>

      {/* Main Grid Content Container (Desktop Split Layout) */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-24 w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">

          {/* 🖼️ 1. Product Display Frame */}
          <div className="md:order-2 flex flex-col items-center justify-center space-y-8">
            
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
              className="
                relative
                w-[260px] h-[340px] 
                sm:w-[320px] sm:h-[420px]
                rounded-[32px]
                bg-white/70 border border-pink-100
                backdrop-blur-md shadow-[0_20px_50px_rgba(255,100,150,0.55),_0_0px_0px_8px_rgba(255,200,210,0.3)]
                overflow-hidden p-2
                transform hover:scale-[1.03] transition duration-300
              "
            >
              <AnimatePresence custom={direction} initial={false}>
                <motion.img
                  key={images[index]}
                  src={images[index]}
                  custom={direction}
                  variants={depthPopVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="w-full h-full object-cover rounded-[24px] absolute top-0 left-0" 
                />
              </AnimatePresence>
            </motion.div>

            {/* Subtle branding below the frame (Desktop only) */}
            <div className="hidden md:block text-center pt-4">
              <p className="text-xl font-medium text-[#8A4A60] tracking-wider italic">
                Handmade with love. Styled for elegance.
              </p>
            </div>
          </div>


          {/* ✍️ 2. Typography & Call-to-Action */}
          <div className="md:order-1 text-center md:text-left space-y-8">
            
            {/* Main Heading */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="space-y-4"
            >
              <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black text-[#4A1F2A] leading-tight">
                ANE
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-rose-400 -mt-2">
                  Crochet
                </span>
              </h1>
              <p className="max-w-xl md:max-w-full mx-auto md:mx-0 text-lg sm:text-xl text-[#6B3A50] font-light leading-relaxed">
                Exquisite, handcrafted pieces that weave comfort and charm into every thread.
                Discover your next treasured heirloom.
              </p>
            </motion.div>


            {/* Buttons */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start pt-4"
            >
              {/* Primary CTA: View Collection */}
              <button
                onClick={scrollToProducts}
                className="
                  w-full sm:w-auto min-w-[200px]
                  px-8 py-4 rounded-full
                  bg-gradient-to-r from-pink-600 to-rose-500
                  text-white text-base font-bold tracking-wider
                  shadow-[0_15px_30px_rgba(255,100,150,0.5)]
                  hover:shadow-[0_20px_40px_rgba(255,100,150,0.6)]
                  hover:scale-[1.03] active:scale-95 transition duration-300 ease-out
                "
              >
                View The Collection
              </button>

              {/* Secondary CTA: WhatsApp */}
              <a
                href="https://wa.me/916001550558"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  w-full sm:w-auto min-w-[200px]
                  px-8 py-4 rounded-full
                  bg-white border-2 border-pink-200
                  text-pink-600 text-base font-semibold
                  flex items-center justify-center gap-2
                  shadow-md hover:shadow-lg active:scale-95 transition
                "
              >
                <span className="text-green-500 text-xl">
                  💬
                </span>
                Chat on WhatsApp
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}